import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkBreaks from "remark-breaks";
import remarkRehype from "remark-rehype";
import rehypeStringify from "rehype-stringify";
import TurndownService from "turndown";

// Flujo compartido de portapapeles para MarkdownEditor. Convierte Markdown en
// texto plano o HTML enriquecido copiado desde herramientas como ChatGPT al
// contenido de Quill, conservando el formato y el fragmento seleccionado antes
// de guardarlo de nuevo como Markdown.

const markdownProcessor = unified()
    .use(remarkParse)
    .use(remarkBreaks)
    .use(remarkRehype)
    .use(rehypeStringify);

const TURNDOWN_OPTIONS = {
    headingStyle: "atx",
    codeBlockStyle: "fenced",
    bulletListMarker: "-",
};

const preserveLineBreaks = {
    filter: ["br"],
    replacement: () => "\n",
};

const turndown = new TurndownService(TURNDOWN_OPTIONS);
turndown.addRule("preserveLineBreaks", preserveLineBreaks);

// Quill muestra los párrafos sin margen: una línea vacía entre ellos conserva la separación.
const spaceParagraphs = (html) => html
    .replace(/<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>)?\s*<\/p>/gi, "")
    .replace(/<\/p>\s*<p(\s[^>]*)?>/gi, "</p><p><br></p><p$1>");

export const markdownToHtml = (markdown) => {
    if (!markdown) return "";
    return spaceParagraphs(String(markdownProcessor.processSync(markdown)));
};

export const htmlToMarkdown = (html) => html ? turndown.turndown(html) : "";

// Algunas fuentes incluyen elementos contenedores fuera de la selección copiada.
const clipboardFragment = (html) => {
    const fragment = html.match(/<!--StartFragment-->([\s\S]*?)<!--EndFragment-->/i);
    return fragment ? fragment[1] : html;
};

export function getClipboardHtml(clipboardData) {
    const html = clipboardData.getData("text/html");
    if (html) {
        // ChatGPT o Claude copian cada párrafo como <p> seguido, sin línea vacía entre ellos.
        return spaceParagraphs(clipboardFragment(html));
    }
    return markdownToHtml(clipboardData.getData("text/plain"));
}

// Pegado en un textarea de texto plano (prompts): el HTML copiado de ChatGPT, Claude,
// Gemini o Google Docs se guarda como Markdown legible, sin escapes ni sangrías de turndown.
const plainTurndown = new TurndownService(TURNDOWN_OPTIONS);
plainTurndown.escape = (text) => text;
plainTurndown.addRule("preserveLineBreaks", preserveLineBreaks);

const isBoldStyle = (node) => /font-weight:\s*(bold|[6-9]00)/i.test(node.getAttribute("style") || "");
const isItalicStyle = (node) => /font-style:\s*italic/i.test(node.getAttribute("style") || "");

// Google Docs envuelve toda la selección en <b style="font-weight:normal">.
plainTurndown.addRule("docsWrapper", {
    filter: (node) => node.nodeName === "B" && /font-weight:\s*normal/i.test(node.getAttribute("style") || ""),
    replacement: (content) => content,
});

// Google Docs marca negrita y cursiva con estilos en <span>.
plainTurndown.addRule("styledSpan", {
    filter: (node) => node.nodeName === "SPAN" && (isBoldStyle(node) || isItalicStyle(node)),
    replacement: (content, node) => {
        const text = content.trim();
        if (!text) return content;
        let wrapped = text;
        if (isItalicStyle(node)) wrapped = `_${wrapped}_`;
        if (isBoldStyle(node)) wrapped = `**${wrapped}**`;
        return content.replace(text, wrapped);
    },
});

plainTurndown.addRule("compactListItem", {
    filter: "li",
    replacement: (content, node) => {
        const parent = node.parentNode;
        let marker = "- ";
        if (parent.nodeName === "OL") {
            const start = Number(parent.getAttribute("start")) || 1;
            marker = `${start + Array.prototype.indexOf.call(parent.children, node)}. `;
        }
        const body = content
            .replace(/^\n+|\s+$/g, "")
            .replace(/\n{2,}/g, "\n")
            .replace(/\n/g, `\n${" ".repeat(marker.length)}`);
        return `${marker}${body}${node.nextSibling ? "\n" : ""}`;
    },
});

const cellText = (content) => content.replace(/\s*\n+\s*/g, " ").replace(/\|/g, "\\|").trim();

plainTurndown.addRule("tableCell", {
    filter: ["th", "td"],
    replacement: (content) => ` ${cellText(content)} |`,
});

plainTurndown.addRule("tableRow", {
    filter: "tr",
    replacement: (content, node) => {
        const row = `\n|${content}`;
        const isHeader = node.parentNode.nodeName === "THEAD"
            || (!node.previousElementSibling && !node.closest("table")?.querySelector("thead"));
        if (!isHeader) return row;
        return `${row}\n|${" --- |".repeat(node.children.length)}`;
    },
});

plainTurndown.addRule("table", {
    filter: "table",
    replacement: (content) => `\n\n${content.replace(/\n{2,}/g, "\n").trim()}\n\n`,
});

plainTurndown.addRule("tableSection", {
    filter: ["thead", "tbody", "tfoot"],
    replacement: (content) => content,
});

const FORMATTING_SELECTOR = "h1,h2,h3,h4,h5,h6,strong,b:not([style*='font-weight:normal']),em,i,ul,ol,li,a[href],pre,code,blockquote,table,hr";

const hasFormatting = (root) => Boolean(root.querySelector(FORMATTING_SELECTOR))
    || Array.from(root.querySelectorAll("span[style]")).some((span) => isBoldStyle(span) || isItalicStyle(span));

const tidyMarkdown = (markdown) => markdown
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/**
 * Markdown for the clipboard's HTML, or null when the HTML carries no formatting
 * (plain spans or paragraphs) and the native text/plain paste is cleaner.
 */
export function clipboardHtmlToMarkdown(clipboardData) {
    const html = clipboardData.getData("text/html");
    if (!html) return null;
    const root = new DOMParser().parseFromString(clipboardFragment(html), "text/html").body;
    root.querySelectorAll("style,script,meta,link,title").forEach((node) => node.remove());
    if (!hasFormatting(root)) return null;
    const markdown = tidyMarkdown(plainTurndown.turndown(root.innerHTML));
    return markdown || null;
}

export function pasteIntoMarkdownEditor(quill, clipboardData, Delta) {
    const html = getClipboardHtml(clipboardData);
    if (!html) return false;
    const range = quill.getSelection(true);
    if (!range) return false;

    const pasted = quill.clipboard.convert(html);
    // Mantiene el último párrafo de un bloque pegado fuera de la lista de destino.
    const lastOp = pasted.ops[pasted.ops.length - 1];
    if (/<(?:p|div|h[1-6]|ul|ol|li|pre|blockquote)(?:\s|>)/i.test(html)
        && lastOp && typeof lastOp.insert === "string" && !lastOp.insert.endsWith("\n")) {
        pasted.insert("\n");
    }
    const change = new Delta().retain(range.index).delete(range.length).concat(pasted);
    const pastedEnd = pasted.ops[pasted.ops.length - 1];
    if (typeof pastedEnd?.insert === "string" && pastedEnd.insert.endsWith("\n")
        && quill.getText(range.index + range.length, 1) === "\n") {
        // El salto de línea final del destino se conserva al seleccionar texto.
        // Evita dejar un elemento numerado extra al sustituir el contenido de una lista.
        change.retain(1, { list: null });
    }
    quill.history.cutoff();
    quill.updateContents(change, "user");
    quill.history.cutoff();
    quill.setSelection(Math.min(range.index + pasted.length(), quill.getLength() - 1), 0, "silent");
    return true;
}
